"use client";

import React, { useState, useRef, useEffect } from "react";
import { jsPDF } from "jspdf";
// Remove the separate import for jspdf-autotable and use the correct import pattern
import "jspdf-autotable";
import { Download } from "lucide-react";
import {
  getOccupancyLabel,
  getPurposeLabel,
  getPropertyTypeLabel,
  getIncomeTypeLabel,
} from "../lib/program-config.ts";
import { Checkbox } from "./ui/checkbox.tsx";

// Function to format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  }).format(amount);
};

interface SharePopupProps {
  isOpen: boolean;
  onClose: () => void;
  loanInfo: {
    program: string;
    rate: number;
    monthlyPayment: number;
    lenderCredit: number;
    price?: number | string;
    fico: number;
    ltv: number;
    loanAmount: number;
    citizenship: string;
    incomeType: string;
    occupancy: string;
    purpose: string;
    amortizationType: string;
    prepaymentPenalty: string;
    creditEvent: string;
    propertyType: string;
    brokerCompensation: string;
    mortgageHistory: string;
    state: string;
    disqualified?: boolean;
    disqualifyAdjustments?: string[];
    allAdjustments?: { name: string; type: string; value: number }[];
    rateStack?: { rate: number; price: number; monthlyPayment: number }[];
  };
}

export function SharePopup({ isOpen, onClose, loanInfo }: SharePopupProps) {
  // Broker user information
  const [brokerCompany, setBrokerCompany] = useState("");
  const [brokerName, setBrokerName] = useState("");
  const [brokerTitle, setBrokerTitle] = useState("");
  const [brokerPhone, setBrokerPhone] = useState("");
  const [brokerEmail, setBrokerEmail] = useState("");

  // Form states
  const [doNotCall, setDoNotCall] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [saveInfo, setSaveInfo] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  // Add validation states for each required field
  const [brokerCompanyError, setBrokerCompanyError] = useState<string | null>(
    null
  );
  const [brokerNameError, setBrokerNameError] = useState<string | null>(null);
  const [brokerTitleError, setBrokerTitleError] = useState<string | null>(null);
  const [brokerPhoneError, setBrokerPhoneError] = useState<string | null>(null);
  const [brokerEmailError, setBrokerEmailError] = useState<string | null>(null);

  // Local storage key for saved user information
  const STORAGE_KEY = "thelender_user_info";

  // Function to save user info to localStorage
  const saveUserInfo = () => {
    try {
      const userInfo = {
        brokerCompany,
        brokerName,
        brokerTitle,
        brokerPhone,
        brokerEmail,
        doNotCall,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(userInfo));
    } catch (error) {
      console.error("Error saving user info:", error);
    }
  };

  // Function to load user info from localStorage
  const loadUserInfo = () => {
    try {
      const savedInfo = localStorage.getItem(STORAGE_KEY);
      if (savedInfo) {
        const userInfo = JSON.parse(savedInfo);
        setBrokerCompany(userInfo.brokerCompany || "");
        setBrokerName(userInfo.brokerName || "");
        setBrokerTitle(userInfo.brokerTitle || "");
        setBrokerPhone(userInfo.brokerPhone || "");
        setBrokerEmail(userInfo.brokerEmail || "");
        setDoNotCall(userInfo.doNotCall || false);
        return true; // Info was loaded
      }
    } catch (error) {
      console.error("Error loading saved user info:", error);
    }
    return false; // No info was loaded
  };

  // Load saved info on component mount
  useEffect(() => {
    if (isOpen) {
      loadUserInfo();
    }
  }, [isOpen]);

  // Close popup when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Don't close if clicking on a select dropdown or option
      const target = event.target as HTMLElement;
      if (
        target.closest('[role="combobox"]') ||
        target.closest('[role="option"]')
      ) {
        return;
      }

      // Only close if clicking outside the popup
      if (popupRef.current && !popupRef.current.contains(target)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle ESC key press
  useEffect(() => {
    function handleEscKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleEscKey);
    }
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Add a function to validate email format with more comprehensive checks
  const isValidEmail = (email: string) => {
    // Basic format check
    const basicFormatRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!basicFormatRegex.test(email)) {
      return { valid: false, message: "Please enter a valid email address" };
    }

    // More specific checks
    const parts = email.split("@");
    const localPart = parts[0];
    const domainPart = parts[1];

    // Check local part length
    if (localPart.length > 64) {
      return { valid: false, message: "The part before @ is too long" };
    }

    // Check for consecutive dots
    if (email.includes("..")) {
      return {
        valid: false,
        message: "Your Email cannot contain consecutive dots",
      };
    }

    // Check domain part
    if (!domainPart.includes(".")) {
      return {
        valid: false,
        message: "Your Email domain is missing a dot",
      };
    }

    // Check TLD (Top Level Domain)
    const tld = domainPart.split(".").pop() || "";
    if (tld.length < 2) {
      return {
        valid: false,
        message: "Your Email domain extension is too short",
      };
    }

    // Check for common typos in popular domains
    const commonDomains = {
      gmial: "gmail",
      gmal: "gmail",
      gamil: "gmail",
      gnail: "gmail",
      hotmial: "hotmail",
      hotmal: "hotmail",
      yaho: "yahoo",
      ymail: "yahoo",
      outloo: "outlook",
      outlok: "outlook",
    };

    const domainWithoutTld = domainPart.split(".")[0];
    if (domainWithoutTld in commonDomains) {
      return {
        valid: false,
        message: `Did you mean ${
          commonDomains[domainWithoutTld as keyof typeof commonDomains]
        }.com?`,
      };
    }

    return { valid: true, message: "" };
  };

  // Add a function to validate phone number with extension
  const isValidPhone = (phone: string) => {
    // Remove all non-digit characters except x for extension
    const cleaned = phone.replace(/[^\dx]/gi, "");

    // Check if we have at least 10 digits (main number)
    const digits = cleaned.replace(/x/gi, "");
    if (digits.length < 10) {
      return false;
    }

    // Check if extension is valid (if present)
    if (cleaned.includes("x")) {
      const parts = cleaned.split(/x/i);
      if (parts.length !== 2 || parts[1].length === 0) {
        return false;
      }
    }

    return true;
  };

  const handleDownloadPDF = async () => {
    // Reset all error states
    setBrokerCompanyError(null);
    setBrokerNameError(null);
    setBrokerTitleError(null);
    setBrokerPhoneError(null);
    setBrokerEmailError(null);
    setFormError(null);
    setDownloadSuccess(false);

    // Validate all required fields
    let hasError = false;

    // Validate broker information
    if (!brokerCompany.trim()) {
      setBrokerCompanyError("Your Company is required");
      hasError = true;
    }

    if (!brokerName.trim()) {
      setBrokerNameError("Your Name is required");
      hasError = true;
    }

    if (!brokerTitle.trim()) {
      setBrokerTitleError("Your Title/Role is required");
      hasError = true;
    }

    if (!brokerPhone.trim()) {
      setBrokerPhoneError("Your Phone Number is required");
      hasError = true;
    } else if (!isValidPhone(brokerPhone)) {
      setBrokerPhoneError(
        "Please enter a valid phone number (extensions allowed)"
      );
      hasError = true;
    }

    if (!brokerEmail.trim()) {
      setBrokerEmailError("Your Email is required");
      hasError = true;
    } else {
      const emailValidation = isValidEmail(brokerEmail);
      if (!emailValidation.valid) {
        setBrokerEmailError(emailValidation.message);
        hasError = true;
      }
    }

    if (!acceptTerms) {
      setFormError("You must accept the Privacy Policy to download the PDF");
      hasError = true;
    }

    if (hasError) {
      return;
    }

    setIsGenerating(true);

    try {
      // Create PDF document
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      // Generate unique quote number (10+ characters)
      const generateQuoteNumber = () => {
        const timestamp = Date.now().toString().slice(-8); // Last 8 digits of timestamp
        const random = Math.random().toString(36).substring(2, 6).toUpperCase(); // 4 random chars
        return `QT${timestamp}${random}`; // Format: QT + 8 digits + 4 chars = 14 characters
      };
      const quoteNumber = generateQuoteNumber();

      // Track pages for footer
      let totalPages = 1; // Start with 1 page, will increment as pages are added

      // Function to add footer with disclaimer and quote number/page count
      const addFooter = (currentPage: number, totalPageCount: number) => {
        const pageHeight = 297; // A4 height in mm
        const pageWidth = 210; // A4 width in mm
        const footerStartY = pageHeight - 20; // Start footer 20mm from bottom

        // Add disclaimer text
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100); // Gray color
        const disclaimerLine1 =
          "This document is for informational purposes only and does not constitute a loan commitment.";
        const disclaimerLine2 =
          "Rates and terms are subject to change without notice. Contact theLender for the most current information.";

        doc.text(disclaimerLine1, pageWidth / 2, footerStartY, {
          align: "center",
        });
        doc.text(disclaimerLine2, pageWidth / 2, footerStartY + 4, {
          align: "center",
        });

        // Add quote number and page count below disclaimer
        doc.setFontSize(10);
        doc.setTextColor(128, 128, 128); // Slightly darker gray
        const footerText = `Quote ${quoteNumber} - Page ${currentPage} of ${totalPageCount}`;
        doc.text(footerText, pageWidth / 2, footerStartY + 10, {
          align: "center",
        });

        doc.setTextColor(0, 0, 0); // Reset to black
      };

      // Add title and date with time
      const currentDate = new Date().toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
      const currentTime = new Date().toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      // Add company name with styling
      doc.setFontSize(24);
      doc.setTextColor(230, 0, 43); // theLender red
      doc.text("theLender", 20, 20);

      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text("Quick Pricer Result", 20, 30);

      doc.setFontSize(12);
      doc.text(`Quote as of ${currentDate} at ${currentTime}`, 20, 40);

      // Add broker information
      let currentY = 50;
      doc.setFontSize(14);
      doc.text("Broker Information:", 20, currentY);
      currentY += 10;

      doc.setFontSize(10);
      doc.text(`Company: ${brokerCompany}`, 20, currentY);
      currentY += 6;
      doc.text(`Name: ${brokerName}`, 20, currentY);
      currentY += 6;
      doc.text(`Title/Role: ${brokerTitle}`, 20, currentY);
      currentY += 6;
      doc.text(`Phone: ${brokerPhone}`, 20, currentY);
      currentY += 6;
      doc.text(`Email: ${brokerEmail}`, 20, currentY);

      // Add loan program
      currentY += 10;
      doc.setFontSize(14);
      doc.text(loanInfo.program, 20, currentY);

      // Add DISQUALIFIED text in red if applicable
      if (
        loanInfo.disqualifyAdjustments &&
        loanInfo.disqualifyAdjustments.length > 0
      ) {
        const programWidth = doc.getTextWidth(loanInfo.program);
        doc.setTextColor(255, 0, 0); // Red color
        doc.text(" DISQUALIFIED", 20 + programWidth, currentY);
        doc.setTextColor(0, 0, 0); // Reset to black

        // Add disqualification reasons below
        currentY += 8;
        doc.setFontSize(10);
        doc.setTextColor(255, 0, 0); // Red color
        loanInfo.disqualifyAdjustments.forEach((reason) => {
          doc.text(reason, 20, currentY);
          currentY += 5;
        });
        doc.setTextColor(0, 0, 0); // Reset to black
        doc.setFontSize(12); // Reset font size
      }

      // Create a simple table for rate, payment, and lender credit
      doc.setFontSize(12);
      doc.setDrawColor(200, 200, 200);

      // Table headers
      const startY = currentY + 10;
      const colWidth = 60;
      const pageWidth = 210; // A4 page width in mm
      const tableWidth = colWidth * 3; // 3 columns
      const startX = (pageWidth - tableWidth) / 2; // Center the table

      // Draw header row
      doc.setFillColor(240, 240, 240);
      doc.rect(startX, startY, colWidth, 10, "F");
      doc.rect(startX + colWidth, startY, colWidth, 10, "F");
      doc.rect(startX + colWidth * 2, startY, colWidth, 10, "F");

      doc.setTextColor(0, 0, 0);
      doc.text("Rate", startX + colWidth / 2, startY + 6, { align: "center" });
      doc.text(
        "Monthly Payment",
        startX + colWidth + colWidth / 2,
        startY + 6,
        {
          align: "center",
        }
      );
      if (Number(loanInfo.price!) > 100) {
        doc.text(
          "Price/Credit",
          startX + colWidth * 2 + colWidth / 2,
          startY + 6,
          {
            align: "center",
          }
        );
      } else if (Number(loanInfo.price!) === 100) {
        doc.text("Price", startX + colWidth * 2 + colWidth / 2, startY + 6, {
          align: "center",
        });
      } else {
        doc.text(
          "Price/Discount",
          startX + colWidth * 2 + colWidth / 2,
          startY + 6,
          {
            align: "center",
          }
        );
      }

      // Draw data row
      doc.rect(startX, startY + 10, colWidth, 10);
      doc.rect(startX + colWidth, startY + 10, colWidth, 10);
      doc.rect(startX + colWidth * 2, startY + 10, colWidth, 10);

      doc.text(
        loanInfo.rate.toFixed(3) + "%",
        startX + colWidth / 2,
        startY + 16,
        {
          align: "center",
        }
      );
      doc.text(
        formatCurrency(loanInfo.monthlyPayment),
        startX + colWidth + colWidth / 2,
        startY + 16,
        {
          align: "center",
        }
      );
      doc.text(
        Number(loanInfo.price!).toFixed(3) || "N/A",
        startX + colWidth * 2 + colWidth / 2,
        startY + 16,
        { align: "center" }
      );

      // Add loan details section
      currentY = startY + 30;

      doc.setFontSize(14);
      doc.text("Loan Details", 20, currentY);
      currentY += 10;

      // Create a 2-column layout for loan details
      const detailsStartY = currentY;
      const detailsColWidth = 85;
      const lineHeight = 8;

      // First column
      doc.setFontSize(10);
      doc.text(`Citizenship: ${loanInfo.citizenship}`, 20, currentY);
      currentY += lineHeight;
      doc.text(`FICO: ${loanInfo.fico}`, 20, currentY);
      currentY += lineHeight;
      doc.text(
        `${loanInfo.program.includes("2nd") ? "CLTV" : "LTV"}: ${loanInfo.ltv}`,
        20,
        currentY
      );
      currentY += lineHeight;
      doc.text(
        `Loan Amount: ${formatCurrency(loanInfo.loanAmount)}`,
        20,
        currentY
      );
      currentY += lineHeight;
      doc.text(
        `Property Type: ${getPropertyTypeLabel(loanInfo.propertyType)}`,
        20,
        currentY
      );
      currentY += lineHeight;
      doc.text(
        `Mortgage History: ${loanInfo.mortgageHistory || "N/A"}`,
        20,
        currentY
      );

      // Second column
      currentY = detailsStartY;
      doc.text(
        `Income Type: ${getIncomeTypeLabel(loanInfo.incomeType)}`,
        20 + detailsColWidth,
        currentY
      );
      currentY += lineHeight;
      doc.text(
        `Amortization Type: ${loanInfo.amortizationType}`,
        20 + detailsColWidth,
        currentY
      );
      currentY += lineHeight;
      doc.text(
        `Prepayment Penalty: ${loanInfo.prepaymentPenalty || "None"}`,
        20 + detailsColWidth,
        currentY
      );
      currentY += lineHeight;
      doc.text(
        `Credit Event: ${loanInfo.creditEvent || "None"}`,
        20 + detailsColWidth,
        currentY
      );
      currentY += lineHeight;
      doc.text(
        `Broker Compensation: ${loanInfo.brokerCompensation}`,
        20 + detailsColWidth,
        currentY
      );
      currentY += lineHeight;
      doc.text(`State: ${loanInfo.state}`, 20 + detailsColWidth, currentY);

      // Add occupancy and purpose
      currentY += lineHeight * 2;
      doc.text(
        `Occupancy: ${getOccupancyLabel(loanInfo.occupancy)}`,
        20,
        currentY
      );
      currentY += lineHeight;
      doc.text(`Purpose: ${getPurposeLabel(loanInfo.purpose)}`, 20, currentY);

      // Add Pricing Adjustments section
      if (loanInfo.allAdjustments && loanInfo.allAdjustments.length > 0) {
        // Check if we need a new page for the pricing adjustments
        const adjustmentsHeight = 30 + loanInfo.allAdjustments.length * 6; // Estimated height needed
        const pageHeight = 297; // A4 height in mm
        const footerHeight = 45; // Space needed for footer with disclaimer

        if (currentY + adjustmentsHeight > pageHeight - footerHeight) {
          doc.addPage();
          totalPages++; // Increment page count
          currentY = 20; // Start near top of new page
        }

        currentY += lineHeight * 2;
        doc.setFontSize(14);
        doc.text("Pricing Adjustments", 20, currentY);
        currentY += 10;

        doc.setFontSize(10);
        loanInfo.allAdjustments.forEach((adjustment) => {
          // Check if current adjustment will fit on the page
          const pageHeight = 297; // A4 height in mm
          const footerHeight = 45; // Space needed for footer with disclaimer
          const adjustmentLineHeight = 6;

          if (currentY + adjustmentLineHeight > pageHeight - footerHeight) {
            doc.addPage();
            totalPages++; // Increment page count
            currentY = 20; // Start near top of new page

            // Add section title again on new page
            doc.setFontSize(14);
            doc.text("Pricing Adjustments (continued)", 20, currentY);
            currentY += 10;
            doc.setFontSize(10);
          }

          const adjustmentText = `${adjustment.name}: ${
            adjustment.value > 0 ? "+" : ""
          }${adjustment.value.toFixed(3)}`;
          doc.text(adjustmentText, 20, currentY);
          currentY += adjustmentLineHeight;
        });
      }

      // Add Rate Stack section
      if (loanInfo.rateStack && loanInfo.rateStack.length > 0) {
        // Check if we need a new page for the rate stack
        const rateStackHeight = 30 + loanInfo.rateStack.length * 8; // Estimated height needed
        const pageHeight = 297; // A4 height in mm
        const footerHeight = 45; // Space needed for footer with disclaimer

        if (currentY + rateStackHeight > pageHeight - footerHeight) {
          doc.addPage();
          totalPages++; // Increment page count
          currentY = 20; // Start near top of new page
        }

        currentY += lineHeight * 2;
        doc.setFontSize(14);
        doc.text("Rate Stack", 20, currentY);
        currentY += 10;

        // Create table headers
        doc.setFontSize(10);
        doc.setDrawColor(200, 200, 200);
        const rateStackStartY = currentY;
        const rateStackColWidth = 60;
        const rateStackPageWidth = 210; // A4 page width in mm
        const rateStackTableWidth = rateStackColWidth * 3; // 3 columns
        const rateStackStartX = (rateStackPageWidth - rateStackTableWidth) / 2; // Center the table

        // Draw header row
        doc.setFillColor(240, 240, 240);
        doc.rect(rateStackStartX, rateStackStartY, rateStackColWidth, 8, "F");
        doc.rect(
          rateStackStartX + rateStackColWidth,
          rateStackStartY,
          rateStackColWidth,
          8,
          "F"
        );
        doc.rect(
          rateStackStartX + rateStackColWidth * 2,
          rateStackStartY,
          rateStackColWidth,
          8,
          "F"
        );

        doc.setTextColor(0, 0, 0);
        doc.text(
          "Rate",
          rateStackStartX + rateStackColWidth / 2,
          rateStackStartY + 5,
          {
            align: "center",
          }
        );
        doc.text(
          "Price",
          rateStackStartX + rateStackColWidth + rateStackColWidth / 2,
          rateStackStartY + 5,
          { align: "center" }
        );
        doc.text(
          "Monthly Payment",
          rateStackStartX + rateStackColWidth * 2 + rateStackColWidth / 2,
          rateStackStartY + 5,
          { align: "center" }
        );

        // Draw data rows
        loanInfo.rateStack.forEach((rateData, index) => {
          const rowY = rateStackStartY + 8 + index * 8;

          doc.rect(rateStackStartX, rowY, rateStackColWidth, 8);
          doc.rect(
            rateStackStartX + rateStackColWidth,
            rowY,
            rateStackColWidth,
            8
          );
          doc.rect(
            rateStackStartX + rateStackColWidth * 2,
            rowY,
            rateStackColWidth,
            8
          );

          doc.text(
            rateData.rate.toFixed(3) + "%",
            rateStackStartX + rateStackColWidth / 2,
            rowY + 5,
            { align: "center" }
          );
          doc.text(
            rateData.price.toFixed(3),
            rateStackStartX + rateStackColWidth + rateStackColWidth / 2,
            rowY + 5,
            { align: "center" }
          );
          doc.text(
            formatCurrency(rateData.monthlyPayment),
            rateStackStartX + rateStackColWidth * 2 + rateStackColWidth / 2,
            rowY + 5,
            { align: "center" }
          );
        });

        currentY = rateStackStartY + 8 + loanInfo.rateStack.length * 8 + 10;
      }

      // Add contact info
      currentY += lineHeight * 2;
      doc.setFontSize(12);
      doc.text("Contact your AE for more details.", 20, currentY);
      currentY += lineHeight;
      // Split the text to make the email clickable
      const emailText = "If you don't have an assigned AE, please email: ";
      const emailAddress = "contact@thelender.com";

      doc.text(emailText, 20, currentY);

      // Calculate the position for the email link
      const emailTextWidth = doc.getTextWidth(emailText);
      const emailX = 20 + emailTextWidth;

      // Add the clickable email link in blue
      doc.setTextColor(0, 0, 255); // Set color to blue (RGB)
      doc.textWithLink(emailAddress, emailX, currentY, {
        url: `mailto:${emailAddress}`,
      });
      doc.setTextColor(0, 0, 0); // Reset color to black for subsequent text

      // Check if we need a new page for the footer
      const pageHeight = 297; // A4 height in mm
      const footerHeight = 25; // Space needed for footer with disclaimer
      const minFooterY = pageHeight - footerHeight;

      if (currentY > minFooterY) {
        doc.addPage();
        totalPages++; // Increment page count
        currentY = 20; // Start near top of new page
      }

      // Add footers to all pages
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        doc.setPage(pageNum);
        addFooter(pageNum, totalPages);
      }

      // Generate filename with date and broker name
      const filename = `thelender-quote-${brokerName.replace(/\s+/g, "-")}-${
        new Date().toISOString().split("T")[0]
      }.pdf`;

      // Download the PDF
      doc.save(filename);

      // Upload form data to CSV blob
      try {
        await fetch("/api/upload-csv", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            brokerCompany,
            brokerName,
            brokerTitle,
            brokerPhone,
            brokerEmail,
            doNotCall,
            acceptTerms,
            loanInfo,
          }),
        });
      } catch (err) {
        // Optionally handle upload error (e.g., show a toast)
        console.error("CSV upload failed:", err);
      }

      // Save user info if checkbox is checked
      if (saveInfo) {
        saveUserInfo();
      }

      setDownloadSuccess(true);
      // Close popup after 2 seconds
      setTimeout(() => {
        onClose();
        setDownloadSuccess(false);
      }, 2000);
    } catch (error) {
      console.error("Error generating PDF:", error);
      setFormError(
        "An error occurred while generating the PDF. Please try again."
      );
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      style={{ backdropFilter: "blur(5px)" }}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
        ref={popupRef}
      >
        <div className="flex items-center mb-4">
          <Download className="w-4 h-4 mr-2" />
          <h2 className="text-2xl font-semibold">Download this Quote</h2>
        </div>
        <p className="mb-4">
          Please fill out the form below to download this quote as a PDF.
        </p>

        {/* Success Message */}
        {downloadSuccess && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            <p className="font-semibold">PDF downloaded successfully!</p>
            <p className="text-sm">The quote has been saved to your device</p>
          </div>
        )}

        {/* Broker Information Section */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-3 text-gray-800">
            Your Information
          </h3>

          {/* Your Company Input */}
          <div className="mb-4">
            <label
              htmlFor="brokerCompany"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Your Company:
            </label>
            <input
              type="text"
              id="brokerCompany"
              className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                brokerCompanyError ? "border-red-500" : ""
              }`}
              value={brokerCompany}
              onChange={(e) => setBrokerCompany(e.target.value)}
            />
            {brokerCompanyError && (
              <p className="text-red-500 text-xs italic">
                {brokerCompanyError}
              </p>
            )}
          </div>

          {/* Your Name Input */}
          <div className="mb-4">
            <label
              htmlFor="brokerName"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Your Name:
            </label>
            <input
              type="text"
              id="brokerName"
              className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                brokerNameError ? "border-red-500" : ""
              }`}
              value={brokerName}
              onChange={(e) => setBrokerName(e.target.value)}
            />
            {brokerNameError && (
              <p className="text-red-500 text-xs italic">{brokerNameError}</p>
            )}
          </div>

          {/* Your Title/Role Input */}
          <div className="mb-4">
            <label
              htmlFor="brokerTitle"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Your Title/Role:
            </label>
            <input
              type="text"
              id="brokerTitle"
              className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                brokerTitleError ? "border-red-500" : ""
              }`}
              value={brokerTitle}
              onChange={(e) => setBrokerTitle(e.target.value)}
            />
            {brokerTitleError && (
              <p className="text-red-500 text-xs italic">{brokerTitleError}</p>
            )}
          </div>

          {/* Your Phone Input */}
          <div className="mb-4">
            <label
              htmlFor="brokerPhone"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Your Phone Number:
            </label>
            <input
              type="tel"
              id="brokerPhone"
              className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                brokerPhoneError ? "border-red-500" : ""
              }`}
              value={brokerPhone}
              onChange={(e) => setBrokerPhone(e.target.value)}
              placeholder="(555) 123-4567 x123"
            />
            {brokerPhoneError && (
              <p className="text-red-500 text-xs italic">{brokerPhoneError}</p>
            )}
          </div>

          {/* Do Not Call Checkbox */}
          <div className="mb-4 flex items-center">
            <input
              type="checkbox"
              id="doNotCall"
              className="mr-2 leading-tight"
              checked={doNotCall}
              onChange={(e) => setDoNotCall(e.target.checked)}
            />
            <label htmlFor="doNotCall" className="text-sm text-gray-700">
              Do Not Call
            </label>
          </div>

          {/* Your Email Input */}
          <div className="mb-4">
            <label
              htmlFor="brokerEmail"
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Your Email:
            </label>
            <input
              type="email"
              id="brokerEmail"
              className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
                brokerEmailError ? "border-red-500" : ""
              }`}
              value={brokerEmail}
              onChange={(e) => setBrokerEmail(e.target.value)}
            />
            {brokerEmailError && (
              <p className="text-red-500 text-xs italic">{brokerEmailError}</p>
            )}
          </div>
        </div>

        {/* Save Info Checkbox */}
        <div className="mb-4 flex items-center space-x-2">
          <Checkbox
            id="saveInfo"
            checked={saveInfo}
            onCheckedChange={(checked: boolean) => setSaveInfo(checked)}
          />
          <label
            htmlFor="saveInfo"
            className="text-sm text-gray-700 cursor-pointer select-none"
          >
            Save my info for future quotes
          </label>
        </div>

        {/* Accept Terms Checkbox */}
        <div className="mb-4 flex items-center space-x-2">
          <Checkbox
            id="acceptTerms"
            checked={acceptTerms}
            onCheckedChange={(checked: boolean) => setAcceptTerms(checked)}
          />
          <label
            htmlFor="acceptTerms"
            className="text-sm text-gray-700 cursor-pointer select-none"
          >
            I accept the{" "}
            <a
              href="https://wholesale.thelender.com/privacy/"
              target="_blank"
              className="text-blue-500 hover:underline"
            >
              Privacy Policy
            </a>
          </label>
        </div>

        {/* Form Error Message */}
        {formError && (
          <p className="text-red-500 text-xs italic mb-4">{formError}</p>
        )}

        {/* Buttons */}
        <div className="flex justify-end">
          <button
            className="bg-gray-500 hover:bg-gray-700 text-white py-2 px-4 rounded focus:outline-none focus:shadow-outline mr-2"
            type="button"
            onClick={onClose}
            disabled={isGenerating}
          >
            Close
          </button>
          <button
            className={`py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
              isGenerating
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-thelender-red hover:bg-red-700"
            } text-white`}
            type="button"
            onClick={handleDownloadPDF}
            disabled={isGenerating}
          >
            {isGenerating ? "Generating..." : "Download"}
          </button>
        </div>
      </div>
    </div>
  );
}
